/**
 * Expense Command Repository
 *
 * Handles all write operations for expenses (create, update, delete).
 * This repository is responsible for modifying expense data in the database.
 */

import { BaseRepository, RepositoryResult } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';
import { Expense } from '@/shared/types';
import { mapDatabaseExpenseToExpense } from '@/shared/services/dataMapper';

/**
 * Database Expense entity
 */
interface DatabaseExpense {
  id: string;
  user_id: string;
  category: 'Software' | 'Equipment' | 'Marketing' | 'Office' | 'Travel' | 'Other';
  description: string;
  amount: number;
  date: string;
  receipt_url?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
  currency?: string;
  is_billable?: boolean;
  client_id?: string;
}

/**
 * Expense Command Repository
 * Provides write operations for the expenses table
 */
export class ExpenseCommandRepository extends BaseRepository<Expense> {
  protected tableName = 'expenses' as const;

  /**
   * Create a new expense
   */
  async create(
    userId: string,
    expenseData: Omit<Expense, 'id' | 'createdAt'>
  ): Promise<RepositoryResult<Expense>> {
    try {
      const db = getDbClient();

      const dbData: any = {
        user_id: userId,
        category: expenseData.category,
        description: expenseData.description,
        amount: expenseData.amount,
        date: expenseData.date,
        receipt_url: expenseData.receiptUrl,
        notes: expenseData.notes,
        currency: expenseData.currency,
        is_billable: expenseData.isBillable,
        client_id: expenseData.clientId,
      };

      const { data, error } = await db
        .from(this.tableName)
        .insert(dbData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const expense = mapDatabaseExpenseToExpense(data as DatabaseExpense);

      return {
        success: true,
        data: expense,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'creating expense'),
      };
    }
  }

  /**
   * Update an existing expense
   */
  async update(id: string, expenseData: Partial<Expense>): Promise<RepositoryResult<Expense>> {
    try {
      const db = getDbClient();

      const dbData: any = {
        category: expenseData.category,
        description: expenseData.description,
        amount: expenseData.amount,
        date: expenseData.date,
        receipt_url: expenseData.receiptUrl,
        notes: expenseData.notes,
        currency: expenseData.currency,
        is_billable: expenseData.isBillable,
        client_id: expenseData.clientId,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await db
        .from(this.tableName)
        .update(dbData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return {
          success: false,
          error: 'Expense not found',
        };
      }

      const expense = mapDatabaseExpenseToExpense(data as DatabaseExpense);

      return {
        success: true,
        data: expense,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'updating expense'),
      };
    }
  }

  /**
   * Delete an expense
   */
  async delete(id: string): Promise<RepositoryResult<void>> {
    try {
      const db = getDbClient();

      const { error } = await db
        .from(this.tableName)
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'deleting expense'),
      };
    }
  }

  /**
   * Bulk delete expenses
   */
  async bulkDelete(ids: string[]): Promise<RepositoryResult<void>> {
    try {
      const db = getDbClient();

      const { error } = await db
        .from(this.tableName)
        .delete()
        .in('id', ids);

      if (error) {
        throw error;
      }

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'bulk deleting expenses'),
      };
    }
  }

  /**
   * Update expense category
   */
  async updateCategory(id: string, category: Expense['category']): Promise<RepositoryResult<Expense>> {
    return this.update(id, { category });
  }

  /**
   * Update expense amount
   */
  async updateAmount(id: string, amount: number): Promise<RepositoryResult<Expense>> {
    return this.update(id, { amount });
  }

  /**
   * Toggle billable status
   */
  async toggleBillable(id: string): Promise<RepositoryResult<Expense>> {
    try {
      // Import dynamically to avoid circular dependency
      const { expenseQueryRepository } = await import('./ExpenseQueryRepository');

      // Get current expense
      const result = await expenseQueryRepository.findById(id);

      if (!result.success || !result.data) {
        return {
          success: false,
          error: 'Expense not found',
        };
      }

      // Toggle the billable status
      return this.update(id, { isBillable: !result.data.isBillable });
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'toggling billable status'),
      };
    }
  }
}

// Export singleton instance
export const expenseCommandRepository = new ExpenseCommandRepository();
